/* eslint-disable @next/next/no-img-element */
/** @jsxImportSource @emotion/react */

import { vars } from "@imwebme/clay-token";
import type { ElementType } from "react";
import type { DotBadgeProps, NumberBadgeProps } from "..";
import { DotBadge, NumberBadge } from "..";
import { Clay } from "../utilities";
import { AvatarDummy } from "./Avatar.dummy";
import { useAvatarDotNumberSize, useAvatarDotSize, useAvatarSize } from "./Avatar.hooks";
import { AvatarProvider, useAvatarSizeContext } from "./Avatar.provider";
import type { AvatarProps } from "./Avatar.types";

/**
 * 아바타는 사용자를 시각적으로 표현하는 방법입니다. 모든 아바타 이미지에는 미묘한 색상을 포함하고 있습니다.
 *
 * 인터렉션이 필요한 경우, [Avatar Button](#avatar-button) 을 사용하세요
 */
const Avatar = <Element extends ElementType = "div">({
  src,
  dummyIndex,
  size = "medium",
  selected = false,
  children,
  ...clayProps
}: AvatarProps<Element>) => {
  const avatarSize = useAvatarSize(size);
  const isAvatarButton = !!clayProps?.native?.onClick && clayProps.as === "button";

  const biggerThanLarge = size.includes("large");
  const 사이즈에따른아웃라인값 = biggerThanLarge ? "3px" : "2px";

  return (
    <AvatarProvider size={size}>
      <Clay position="relative" display="inline-block" {...clayProps}>
        <Clay
          shape="circle"
          size={avatarSize}
          overflow="hidden"
          {...(isAvatarButton && {
            _hover: {
              opacity: 0.3,
            },
            _active: {
              opacity: 0.5,
            },
          })}
          {...(selected && {
            outlineWidth: 사이즈에따른아웃라인값,
            outlineStyle: "solid",
            outlineColor: vars.semantic.color.focus,
            outlineOffset: 사이즈에따른아웃라인값,
          })}
        >
          {src ? (
            <img
              src={src}
              alt=""
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          ) : (
            <AvatarDummy index={dummyIndex} />
          )}
        </Clay>
        {children}
      </Clay>
    </AvatarProvider>
  );
};

Avatar.displayName = "Avatar";

const AvatarDot = ({ variant, sx }: Pick<DotBadgeProps, "variant" | "sx">) => {
  const avatarSize = useAvatarSizeContext();
  const { dotSize, offset } = useAvatarDotSize(avatarSize);

  return (
    <DotBadge
      size={dotSize}
      type="border"
      variant={variant}
      sx={{
        position: "absolute",
        top: offset,
        right: offset,
        ...sx,
      }}
    />
  );
};

const AvatarDotNumber = (props: Omit<NumberBadgeProps, "size">) => {
  const avatarSize = useAvatarSizeContext();
  const { offset } = useAvatarDotNumberSize(avatarSize);

  return (
    <NumberBadge
      size="medium"
      {...props}
      sx={{
        position: "absolute",
        top: offset,
        right: offset,
        transform: "translate(50%, -50%)",
        outlineWidth: avatarSize === "medium" ? "3px" : "2px",
        outlineStyle: "solid",
      }}
    />
  );
};

const AvatarNameSpace = Object.assign(Avatar, { Dot: AvatarDot, DotNumber: AvatarDotNumber });

export { AvatarNameSpace as Avatar };
